// ==========================================================================
// Plyr HTML5 helpers
// ==========================================================================

import support from './support';
import { toggleClass, createElement, removeElement } from './utils/elements';
import { on, triggerEvent } from './utils/events';
import is from './utils/is';
import { silencePromise } from './utils/promise';
import { setAspectRatio } from './utils/style';
import { getDate } from './utils/time';
import browser from './utils/browser';
import i18n from './utils/i18n';
import ui from './ui';

const html5 = {

  showMessage(message, passive = true) {
    if (passive && this.message) {
      return Promise.reject(new Error('message already display'));
    }

    if (this.elements.message) {

      ui.togglePoster.call(this, false);

      this.elements.message.removeAttribute('hidden');
      this.elements.text.innerHTML = message;
      toggleClass(this.elements.container, this.config.classNames.messageEnabled, true);
    }
  },

  getSources() {
    if (!this.isHTML5) {
      return [];
    }

    const sources = Array.from(this.media.querySelectorAll('source'));

    // Filter out unsupported sources (if type is specified)
    return sources.filter((source) => {
      const type = source.getAttribute('type');

      if (is.empty(type)) {
        return true;
      }

      return support.mime.call(this, type);
    });
  },

  // Get quality levels
  getQualityOptions() {
    // Whether we're forcing all options (e.g. for streaming)
    if (this.config.quality.forced) {
      return this.config.quality.options;
    }

    // Get sizes from <source> elements
    return html5.getSources
      .call(this)
      .map((source) => Number(source.getAttribute('size')))
      .filter(Boolean);
  },

  setup() {
    if (!this.isHTML5) {
      return;
    }

    const player = this;

    if (player.dash && browser.isSafari) {
      html5.showMessage.call(player, i18n.get('canNotSupportSafari', player.config.starplayer));
      return;
    }


    if (html5.watermarkEnabled.call(player)) {
      on.call(player, player.media, 'playing', (event) => {
        // player.debug.log(event.type);
        if (!is.element(player.elements.watermark)) {
          html5.watermarkRender.call(player);
        }
      });

      on.call(player, player.media, 'ended emptied abort error', (event) => {
        // player.debug.log(event.type);
        html5.watermarkRelease.call(player);
      });
    }

    // Set speed options from config
    player.options.speed = player.config.speed.options; //[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 4]

    // Set aspect ratio if fixed
    if (!is.empty(this.config.ratio)) {
      setAspectRatio.call(player);
    }

    // Quality
    Object.defineProperty(player.media, 'quality', {
      get() {
        // Get sources
        const sources = html5.getSources.call(player);
        const source = sources.find((s) => s.getAttribute('src') === player.source);

        // Return size, if match is found
        return source && Number(source.getAttribute('size'));
      },
      set(input) {
        if (player.quality === input) {
          return;
        }

        // If we're using an an external handler...
        if (player.config.quality.forced && is.function(player.config.quality.onChange)) {
          player.config.quality.onChange(input);
        } else {
          // Get sources
          const sources = html5.getSources.call(player);
          // Get first match for requested size
          const source = sources.find((s) => Number(s.getAttribute('size')) === input);

          // No matching source found
          if (!source) {
            return;
          }

          player.elements.inputs.seek.disabled = true;
          // Get current state
          const { currentTime, paused, preload, readyState, playbackRate } = player.media;

          // Set new source
          player.media.src = source.getAttribute('src');

          // Prevent loading if preload="none" and the current source isn't loaded (#1044)
          if (preload !== 'none' || readyState) {
            // Restore time
            player.once('loadedmetadata', () => {
              player.elements.inputs.seek.disabled = false;
              player.speed = playbackRate;
              player.currentTime = currentTime;

              // Resume playing
              if (!paused) {
                silencePromise(player.play());
              }
            });

            // Load new source
            player.media.load();
          }
        }

        // Trigger change event
        triggerEvent.call(player, player.media, 'qualitychange', false, {
          quality: input,
        });
      },
    });
  },

  // Cancel current network requests
  // See https://github.com/sampotts/plyr/issues/174
  cancelRequests() {
    if (!this.isHTML5) {
      return;
    }

    // Remove child sources
    removeElement(html5.getSources.call(this));

    // Set blank video src attribute
    // This is to prevent a MEDIA_ERR_SRC_NOT_SUPPORTED error
    // Info: http://stackoverflow.com/questions/32231579/how-to-properly-dispose-of-an-html5-video-and-close-socket-or-connection
    this.media.setAttribute('src', this.config.blankVideo);

    // Load the new empty source
    // This will cancel existing requests
    // See https://github.com/sampotts/plyr/issues/174
    this.media.load();

    // Debugging
    this.debug.log('Cancelled network requests');
  },

  watermarkEnabled() {
    const player = this;
    return player.isVideo && player.config.starplayer.watermark.enabled;
  },

  watermarkRender() {
    //html5
    const player = this;
    player.elements.watermark = createElement("div");
    player.elements.wrapper.after(player.elements.watermark);
    let interval =
      player.config.starplayer.watermark.positionChangeInterval;
    const intervalIsValid = is.number(interval) && interval > 0;
    interval = intervalIsValid ? interval : 5;
    let displayDuration =
      player.config.starplayer.watermark.displayDuration;
    const displayDurationIsValid =
      is.number(displayDuration) && displayDuration < interval;
    displayDuration = displayDurationIsValid ? displayDuration : interval;

    if (player.stars.watermarkTimer) {
      clearInterval(player.stars.watermarkTimer);
    }
    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }
    player.stars.watermarkTimer = setInterval(() => {
      if (!player.elements.watermark.parentNode) {
        if (!is.element(player.elements.watermark)) {
          player.elements.watermark = createElement("div");
        }
      }

      player.elements.watermark.innerHTML =
        player.config.starplayer.watermark.text;
      let style = player.elements.watermark.style;
      style.position = "absolute";
      style.width = "auto";
      style.display = "block";
      style.margin = "0";
      style.padding = "0";
      style.zIndex = "9999999";
      style.fontWeight = "bold";
      style.color = player.config.starplayer.watermark.color;
      style.opacity = player.config.starplayer.watermark.opacity;
      style.filter = "opacity(" + style.opacity * 100 + "%)";

      var watermarkHorizon =
        player.config.starplayer.watermark.horizontalAlign.toLowerCase();
      var watermarkVertical =
        player.config.starplayer.watermark.verticalAlign.toLowerCase();
      var horizon, vertical, watermarkNum;
      switch (watermarkHorizon) {
        case "random":
          horizon = 5;
          break;

        case "left":
          horizon = 1;
          break;

        case "right":
          horizon = 2;
          break;
      }

      switch (watermarkVertical) {
        case "random":
          vertical = 8;
          break;

        case "top":
          vertical = 0;
          break;

        case "bottom":
          vertical = 2;
          break;
      }
      switch (horizon + vertical) {
        case 1:
        case 2:
        case 3:
        case 4:
          watermarkNum = horizon + vertical;
          break;

        case 5:
          watermarkNum = html5.watermarkRandom.call(this, 1, 2);
          break;

        case 7:
          watermarkNum = html5.watermarkRandom.call(this, 3, 4);
          break;

        case 9:
          watermarkNum = html5.watermarkRandom.call(this, 1, 3);
          break;

        case 10:
          watermarkNum = html5.watermarkRandom.call(this, 2, 4);  //right , borttom
          break;

        case 13:
          watermarkNum = (Math.floor(Math.random() * 10000) % 4) + 1;
          break;
      }
      html5.watermarkPosition.call(player, watermarkNum);
      player.stars.watermarkDurationTimer = setTimeout(() => {
        style.display = "none";
      }, displayDuration * 1000 - 100);
    }, interval * 1000);
  },

  watermarkRelease() {
    const player = this;
    if (player.elements.watermark) {
      player.elements.watermark.remove();
      player.elements.watermark = null;
    }
    if (player.stars.watermarkTimer) {
      clearInterval(player.stars.watermarkTimer);
    }
    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }
  },

  watermarkRandom(a, b) {
    var watermarkNo = (Math.floor(Math.random() * 10000) % 2) + 1;
    watermarkNo = (watermarkNo == 1) ? a : b;
    return watermarkNo;
  },

  watermarkOnVideo(left, top) {
    const player = this;
    const rect = player.elements.container.getBoundingClientRect();
    // console.log(rect);
    const mediaRatio = player.media.videoWidth / player.media.videoHeight;
    const clientRatio = rect.width / rect.height;
    const playerRatio = player.config.ratio.split(":");
    var videoHeight,
      videoWidth,
      watermarkTop,
      watermarkTopPos,
      paddingHeight, paddingWidth,
      topPadding, leftPadding, watermarkLeft;

      if (mediaRatio.toFixed(2) != clientRatio.toFixed(2)) {
        if (player.fullscreen.active) {
          console.log(mediaRatio.toFixed(2));
          console.log(clientRatio.toFixed(2));

          if (mediaRatio.toFixed(2)> clientRatio.toFixed(2)) {//
              videoHeight = (screen.width / playerRatio[0]) * playerRatio[1];
              paddingHeight = (screen.height - videoHeight) / 2;
              topPadding = (paddingHeight / screen.height) * 100;
              watermarkTop = topPadding + (videoHeight / screen.height) * top;
              watermarkLeft = left;
          }
            else {//
              console.log("가로테두리");
              videoWidth = (screen.height / playerRatio[1]) * playerRatio[0];
              paddingWidth = (screen.width - videoWidth) / 2;
              leftPadding = (paddingWidth / screen.width) * 100;
              watermarkTop = top;
              videoHeight = player.media.clientHeight;
              watermarkLeft = (leftPadding + left) > 88 ? left: (leftPadding + left);

            }
          }
          watermarkTopPos = watermarkTop;
        }
        //watermarkTopPos = watermarkTop;
       else {//일반화면, 비율맞을때
        videoHeight = rect.height;
        videoWidth = rect.width;
        watermarkTopPos = top;
        watermarkLeft = left;
      }
    player.elements.watermark.style.fontSize = `${videoHeight * 0.05}%`;
    player.elements.watermark.style.left = `${watermarkLeft}%`;
    player.elements.watermark.style.top = `${watermarkTopPos}%`;

  },

  watermarkPosition(num) {
    const player = this;
    let left, top;
    switch (num) {
      case 1:
        left = player.config.starplayer.watermark.horizon1;
        left = (left / 100) * 50 >= 50 ? 40 : (left / 100) * 50;
        top =
          player.config.starplayer.watermark.vertical1;
        top = (top / 100) * 50;
        break;

      case 2:
        left = player.config.starplayer.watermark.horizon2;
        left = ((left / 100) * 50 + 50) > 80 ? 80 : ((left / 100) * 50 + 50);
        top = player.config.starplayer.watermark.vertical2;
        top = (top / 100) * 50;
        break;

      case 3:
        left = player.config.starplayer.watermark.horizon3;
        left = (left / 100) * 50 >= 50 ? 40 : (left / 100) * 50;
        top = player.config.starplayer.watermark.vertical3;
        top = (top / 100) * 50 + 50 >= 100 ? 88 : ((top / 100) * 50 + 50);
        break;

      case 4:
        left = player.config.starplayer.watermark.horizon4;
        left = (left / 100) * 50 + 50 >= 100 ? 80 : (left / 100) * 50 + 50;
        top = player.config.starplayer.watermark.vertical4;
        top = ((top / 100) * 50 + 50>= 100) ? 88 : ((top / 100) * 50 + 50);
        break;
    }
    html5.watermarkOnVideo.call(player, left, top);
  }

};

export default html5;
